/**
 * Usuarios de la empresa.
 *
 * Faltaba entera: dar de alta a un técnico exigía entrar al servidor por SSH.
 * Con técnicos que rotan, eso no aguanta el primer mes.
 *
 * Incluye el restablecimiento de contraseña, que es el caso real —alguien la
 * olvida un lunes por la mañana— y que hasta ahora no tenía solución ninguna.
 */

import { useCallback, useEffect, useState } from 'react';
import { ROLES } from '@fibwifire/domain/browser';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { useForm } from '../forms/use-form.js';
import { CheckField, FormSection, TextField } from '../forms/fields.js';
import { maxLength, required, type FieldRules } from '../forms/form-state.js';

interface Row {
  id: string;
  email: string;
  full_name: string;
  status: string;
  mfa_enabled: boolean;
  last_login_at: string | null;
  commission_rate: string | null;
  roles: string[];
}

/** Nombres legibles. Los códigos son del catálogo del servidor. */
const ROLE_LABELS: Record<string, string> = {
  SUPERADMIN: 'Superadministrador',
  DIRECCION: 'Dirección',
  ADMINISTRACION: 'Administración',
  RESPONSABLE_TECNICO: 'Responsable técnico',
  TECNICO: 'Técnico',
  COMERCIAL: 'Comercial',
  ALMACEN: 'Almacén',
  COMPRAS: 'Compras',
  CONTABILIDAD: 'Contabilidad',
  TESORERIA: 'Tesorería',
  LECTURA: 'Sólo lectura',
  CLIENTE: 'Cliente (portal)',
};

const RULES: FieldRules = {
  email: [required('El correo')],
  fullName: [required('El nombre'), maxLength(255)],
  password: [required('La contraseña')],
};

/** Contraseña inicial generada, para que nadie invente una débil. */
function generatePassword(): string {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function UsersScreen(): React.ReactNode {
  const { api, user } = useSession();

  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(['TECNICO']));
  /**
   * La contraseña propuesta se genera UNA vez.
   *
   * `useForm` sincroniza comparando el contenido de los valores iniciales,
   * no la identidad del objeto — así una ficha que llega por red actualiza
   * el formulario. Generar la contraseña dentro de `initial` producía
   * contenido nuevo en cada render, y con él un bucle infinito que además
   * reescribía lo que se estuviera tecleando.
   */
  const [seedPassword, setSeedPassword] = useState(generatePassword);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const data = await api.get<{ items: Row[] }>('/users');
    setRows(data.items);
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    reload()
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'No se han podido cargar los usuarios.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const form = useForm<{ id: string }>({
    mode: 'create',
    rules: RULES,
    initial: { email: '', fullName: '', password: seedPassword },
    onSubmit: async (body, client) =>
      client.request<{ id: string }>('/users', {
        method: 'POST',
        body: JSON.stringify({ ...body, roleCodes: [...selected] }),
      }),
    onSaved: () => {
      setNotice(
        'Usuario creado. Comunique la contraseña por un canal aparte y pídale que la cambie al entrar.',
      );
      // Cambiar la semilla vacía el formulario entero, que es lo que se
      // quiere tras un alta: nombre y correo nuevos, y una contraseña que
      // no se repite entre usuarios.
      setSeedPassword(generatePassword());
      void reload();
    },
  });

  async function resetPassword(row: Row): Promise<void> {
    const next = generatePassword();
    if (
      !globalThis.confirm(
        `¿Restablecer la contraseña de ${row.full_name}?\n\n` +
          `Nueva contraseña: ${next}\n\n` +
          'Se cerrarán todas sus sesiones y quedará registrado quién lo hizo.',
      )
    ) {
      return;
    }

    setError(null);
    try {
      await api.post(`/users/${row.id}/password`, { newPassword: next });
      setNotice(`Contraseña de ${row.full_name} restablecida: ${next}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido restablecer.');
    }
  }

  async function toggleStatus(row: Row): Promise<void> {
    const suspending = row.status === 'ACTIVE';
    if (
      suspending &&
      !globalThis.confirm(`¿Suspender a ${row.full_name}?\n\nSe cerrarán sus sesiones al momento.`)
    ) {
      return;
    }

    setError(null);
    try {
      await api.request(`/users/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: suspending ? 'SUSPENDED' : 'ACTIVE' }),
      });
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido cambiar el estado.');
    }
  }

  if (loading) return <p className="screen__note">Cargando…</p>;

  // Se pregunta por el permiso, no por el nombre del rol: si mañana se
  // concede `identity.create` a otro rol, esta pantalla lo respeta sola.
  const canManage = hasPermission(user, 'identity.create');

  return (
    <section className="screen">
      <h2 className="screen__title">Usuarios</h2>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}

      <div className="scroller">
        <table className="table__grid table__grid--compact">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Correo</th>
              <th>Roles</th>
              <th>Comisión</th>
              <th>Estado</th>
              <th>Último acceso</th>
              {canManage && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.full_name}</td>
                <td>{row.email}</td>
                <td>{row.roles.map((r) => ROLE_LABELS[r] ?? r).join(', ') || '—'}</td>
                <td>
                  {canManage ? (
                    <CommissionCell row={row} onSaved={reload} api={api} />
                  ) : (
                    formatRate(row.commission_rate)
                  )}
                </td>
                <td>
                  {row.status === 'ACTIVE' ? (
                    <span className="pill pill--ok">✓ Activo</span>
                  ) : (
                    <span className="pill pill--off">{row.status === 'SUSPENDED' ? 'Suspendido' : 'Bloqueado'}</span>
                  )}
                </td>
                <td>{row.last_login_at ? new Date(row.last_login_at).toLocaleString('es-ES') : 'Nunca'}</td>
                {canManage && (
                  <td>
                    <button className="button button--quiet" type="button" onClick={() => void resetPassword(row)}>
                      Restablecer contraseña
                    </button>{' '}
                    <button className="button button--quiet" type="button" onClick={() => void toggleStatus(row)}>
                      {row.status === 'ACTIVE' ? 'Suspender' : 'Reactivar'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!canManage ? (
        <p className="screen__note">
          Sólo un superadministrador puede dar de alta usuarios o restablecer contraseñas.
        </p>
      ) : (
        <form className="form" onSubmit={(e) => void form.submit(e)} noValidate>
          {form.formError !== null && (
            <p className="screen__error" role="alert">
              {form.formError}
            </p>
          )}

          <FormSection title="Nuevo usuario">
            <TextField
              label="Nombre y apellidos"
              value={String(form.values['fullName'] ?? '')}
              onChange={(v) => form.set('fullName', v)}
              onBlur={() => form.blur('fullName')}
              error={form.errors['fullName']}
              required
            />
            <TextField
              label="Correo"
              type="email"
              value={String(form.values['email'] ?? '')}
              onChange={(v) => form.set('email', v)}
              onBlur={() => form.blur('email')}
              error={form.errors['email']}
              help="Es su identificador para entrar. Único dentro de la empresa."
              required
            />
            <TextField
              label="Contraseña inicial"
              value={String(form.values['password'] ?? '')}
              onChange={(v) => form.set('password', v)}
              onBlur={() => form.blur('password')}
              error={form.errors['password']}
              help="Generada al azar. Mínimo 12 caracteres. Comuníquela por un canal aparte."
              required
            />
            <button
              className="button button--quiet"
              type="button"
              onClick={() => form.set('password', generatePassword())}
            >
              Generar otra
            </button>
          </FormSection>

          <FormSection title="Roles">
            {/* Sin rol, el usuario entra y no puede hacer nada: un fallo que
                tarda días en descubrirse porque parece de permisos. El
                servidor lo rechaza; aquí se avisa antes. */}
            {selected.size === 0 && (
              <p className="screen__note">Seleccione al menos uno.</p>
            )}
            {ROLES.filter((r) => r !== 'CLIENTE').map((role) => (
              <CheckField
                key={role}
                label={ROLE_LABELS[role] ?? role}
                checked={selected.has(role)}
                onChange={(on) =>
                  setSelected((current) => {
                    const next = new Set(current);
                    if (on) next.add(role);
                    else next.delete(role);
                    return next;
                  })
                }
              />
            ))}
          </FormSection>

          <button
            className="button button--primary"
            type="submit"
            disabled={form.saving || selected.size === 0}
          >
            {form.saving ? 'Creando…' : 'Crear usuario'}
          </button>
        </form>
      )}
    </section>
  );
}

/** El porcentaje, de fracción a tanto por ciento legible. */
function formatRate(rate: string | null): string {
  if (rate === null) return '—';
  return `${(Number(rate) * 100).toFixed(2).replace(/\.?0+$/, '').replace('.', ',')} %`;
}

/**
 * Porcentaje de comisión, editable en la propia fila.
 *
 * Se escribe en tanto por ciento —«5», que es como lo dice todo el mundo— y
 * se guarda como fracción, que es como lo necesita el cálculo. Vaciarlo lo
 * deja sin comisión, que no es lo mismo que ponerle cero: sin porcentaje el
 * comercial aparece marcado en el informe como pendiente de asignar, y con
 * cero aparece como que cobra cero.
 */
function CommissionCell({
  row,
  api,
  onSaved,
}: {
  row: Row;
  api: ReturnType<typeof useSession>['api'];
  onSaved: () => Promise<void> | void;
}): React.ReactNode {
  const [value, setValue] = useState(row.commission_rate === null ? '' : String(Number(row.commission_rate) * 100));
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  async function save(): Promise<void> {
    const trimmed = value.trim().replace(',', '.');
    const current = row.commission_rate === null ? '' : String(Number(row.commission_rate) * 100);
    if (trimmed === current.trim()) return;

    setSaving(true);
    setFailed(false);
    try {
      const rate = trimmed === '' ? null : (Number(trimmed) / 100).toFixed(4);
      await api.request<void>(`/users/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ commissionRate: rate }),
      });
      await onSaved();
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <span className="rate-cell">
      <input
        type="number"
        min="0"
        max="100"
        step="0.01"
        value={value}
        disabled={saving}
        aria-label={`Porcentaje de comisión de ${row.full_name}`}
        aria-invalid={failed}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => void save()}
      />
      %{failed ? <span className="due--late"> no guardado</span> : null}
    </span>
  );
}
